import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from '../components/ProfileImage';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
import EffectiveDate from '../components/legal/EffectiveDate';
// PromptBar import removed per Miles 2026-04-06

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Header for navigation
const HomeHeader = styled.header`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  &.active {
    color: var(--brand-gold);
    background-color: rgba(198, 167, 94, 0.08);

    svg {
      opacity: 1;
      color: var(--brand-gold);
    }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

// Mobile menu components
const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const MobileMenuIcon = styled.div<{ $isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-black);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;

    &:nth-child(1) {
      top: ${props => props.$isOpen ? '9px' : '4px'};
      transform: ${props => props.$isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.$isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      top: ${props => props.$isOpen ? '9px' : '14px'};
      transform: ${props => props.$isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem 0;
  z-index: 200000;
  flex-direction: column;
  width: 200px;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.25s ease-out;

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 180px;
    top: 3.5rem;
    right: 0.75rem;
    padding: 0.75rem 0;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--brand-black);

    svg {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--brand-black);

    svg {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const PageContainer = styled.div`
  padding: 6rem 2.5rem 0;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: var(--brand-canvas);
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 5rem 1.5rem 0;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 0;
  }
`;

const CookieHero = styled.div`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  color: white;
  padding: 5rem 4rem;
  border-radius: 20px;
  margin-bottom: 4rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(26, 26, 26, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
  width: 100%;

  @media (max-width: 768px) {
    padding: 3.5rem 2rem;
    margin-bottom: 3rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1rem;
    margin-bottom: 2.5rem;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.07' fill-rule='evenodd'/%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70%;
    background: linear-gradient(to top, rgba(26, 26, 26, 0.2) 0%, transparent 100%);
    z-index: 1;
  }
`;

const CookieTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 2rem;
  line-height: 1.2;
  position: relative;
  z-index: 2;
  letter-spacing: -0.02em;
  max-width: 900px;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.4rem;
    margin-bottom: 1.5rem;
    line-height: 1.3;
    max-width: 100%;
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.25rem;
    line-height: 1.35;
    padding: 0;
  }
`;

const HeroTagline = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 400;
  max-width: 700px;
  position: relative;
  z-index: 2;
  opacity: 0.9;
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    line-height: 1.4;
  }
`;

const ContentContainer = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  margin-bottom: 4rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
    margin-bottom: 3rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    margin-bottom: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: var(--brand-black);
  margin-bottom: 1.5rem;
  margin-top: 3.5rem;
  animation: ${fadeInLeft} 0.6s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
  position: relative;
  display: inline-block;
  font-family: 'Satoshi', sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-top: 2.75rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-top: 2.25rem;
    margin-bottom: 1rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, var(--brand-gold), var(--brand-gold-hover));
    border-radius: 2px;

    @media (max-width: 768px) {
      width: 50px;
      height: 2px;
      bottom: -8px;
    }

    @media (max-width: 480px) {
      width: 40px;
      bottom: -6px;
    }
  }
`;

const SubSectionTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--brand-black);
  margin-bottom: 1rem;
  margin-top: 2rem;
  position: relative;
  display: inline-block;
  font-family: 'Satoshi', sans-serif;
  font-weight: 600;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 1.35rem;
    margin-bottom: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    margin-bottom: 0.8rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, var(--brand-gold), var(--brand-black));
    border-radius: 2px;

    @media (max-width: 768px) {
      width: 35px;
      bottom: -4px;
    }

    @media (max-width: 480px) {
      width: 30px;
      height: 1.5px;
      bottom: -3px;
    }
  }
`;

const CookieText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--brand-text-secondary);
  margin-bottom: 1.5rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: 0.3s;
  animation-fill-mode: both;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    line-height: 1.7;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.65;
    margin-bottom: 1.15rem;
  }
`;

const CookieList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 0;
  list-style: none;
`;

const CookieListItem = styled.li`
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--brand-text-secondary);
  margin-bottom: 0.8rem;
  position: relative;
  padding-left: 1.8rem;
  list-style-type: none;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 0.7rem;
    padding-left: 1.6rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 0.65rem;
    padding-left: 1.4rem;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.7rem;
    width: 8px;
    height: 8px;
    background: linear-gradient(90deg, var(--brand-gold), var(--brand-gold-hover));
    border-radius: 50%;

    @media (max-width: 768px) {
      width: 7px;
      height: 7px;
      top: 0.65rem;
    }

    @media (max-width: 480px) {
      width: 6px;
      height: 6px;
      top: 0.55rem;
    }
  }
`;

const QuickFactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin: 2.5rem 0 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    margin: 2rem 0 0.75rem;
  }
`;

const QuickFactCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);

    @media (max-width: 768px) {
      transform: translateY(-3px);
    }

    @media (max-width: 480px) {
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    padding: 1.75rem;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const QuickFactLabel = styled.span`
  font-size: 0.85rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: var(--brand-gold);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
`;

const QuickFactValue = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--brand-black);
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Callout = styled.div`
  margin: 2.5rem 0;
  padding: 1.75rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.08), rgba(26, 26, 26, 0.04));
  border: 1px solid rgba(198, 167, 94, 0.2);
  color: var(--brand-text-secondary);
  line-height: 1.7;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.05rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--brand-gold), var(--brand-gold-hover));
    border-radius: 2px 0 0 2px;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 2rem 0;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin: 1.75rem 0;
    font-size: 0.95rem;
  }
`;

const CookieTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--brand-line);
`;

const CookieTableHeadCell = styled.th`
  text-align: left;
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  color: var(--brand-canvas);
  padding: 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  font-family: 'Space Grotesk', sans-serif;
  border-bottom: 2px solid var(--brand-gold);
`;

const CookieTableCell = styled.td`
  padding: 1.25rem;
  font-size: 1rem;
  color: var(--brand-text-secondary);
  line-height: 1.7;
  border-bottom: 1px solid var(--brand-line);
  vertical-align: top;
  background: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:last-child {
    width: 35%;
  }

  &:nth-child(1) {
    font-weight: 600;
    color: var(--brand-black);
    font-family: 'Satoshi', sans-serif;
  }
`;

const CookieOrderedList = styled.ol`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  counter-reset: ordered-list;
  list-style: none;
`;

const CookieOrderedListItem = styled.li`
  margin-bottom: 1rem;
  padding-left: 0.5rem;
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  counter-increment: ordered-list;
  position: relative;

  &::before {
    content: counter(ordered-list);
    position: absolute;
    left: -1.5rem;
    top: 0;
    color: var(--brand-gold);
    font-weight: 700;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.55;
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

const Cookie: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const timeouts = [
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 300),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 500)
    ];

    return () => {
      timeouts.forEach(id => clearTimeout(id));
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToLogin = () => {
    navigate('/');
    setTimeout(() => {
      const loginSection = document.getElementById('login-section');
      loginSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
    <PageContainer>
      <SeoHead
        title="Cookie Policy | prest0"
        tabTitle="cookie policy"
        description="Understand how prest0 uses cookies and tracking technologies across our enterprise legal AI platform."
        keywords="cookie policy, prest0 cookies, enterprise legal AI cookies, legal platform tracking"
        canonicalUrl="https://prest0.ai/cookie-policy"
        schemaType="WebPage"
      />

      {/* Desktop Navigation */}
      <HomeHeader>
        
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          insights
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </HeaderIconLink>
        {user ? (
          <>
            <ProfileImage size={32} />
            <NotificationsBell />
          </>
        ) : (
          <HeaderIconButton onClick={scrollToLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </HeaderIconButton>
        )}
      </HomeHeader>

      {/* Mobile Navigation */}
      <MobileMenuToggle onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
        <MobileMenuIcon $isOpen={menuOpen}>
          <span />
          <span />
          <span />
        </MobileMenuIcon>
      </MobileMenuToggle>

      <MobileMenuDropdown $isOpen={menuOpen} ref={mobileMenuRef}>
        
        <MobileMenuLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </MobileMenuLink>
        <MobileMenuLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          insights
        </MobileMenuLink>
        <MobileMenuLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </MobileMenuLink>
        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/profile" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              profile
            </MobileMenuLink>
            <MobileNotifications />
            <MobileMenuButton onClick={() => { logout(); setMenuOpen(false); }} style={{ color: '#ff4d4f' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileMenuButton>
          </>
        ) : (
          <MobileMenuButton onClick={scrollToLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

      <CookieHero>
        <CookieTitle>cookie policy</CookieTitle>
        <HeroTagline>how prest0 uses cookies and similar technologies to protect and personalize your experience</HeroTagline>
        <EffectiveDate document="cookie" />
      </CookieHero>

      <ContentContainer>
        <CookieText>
          prest0 uses cookies and similar technologies to provide, maintain, and continually improve our services. This cookie policy explains what data we collect through these technologies, how we use it, and the options available to you.
        </CookieText>
        <CookieText>
          The way we set, store, and honor cookies lines up with the commitments on our security page: TLS 1.3 with HSTS protects every session, data at rest is encrypted with AES-256 on Google Cloud, enterprise OAuth 2.0 with MFA guards account access, prompts never train shared models (contractual no-training guarantee), and documented retention schedules govern storage lifecycles.
        </CookieText>

        <QuickFactsGrid>
          <QuickFactCard>
            <QuickFactLabel>cookie scope</QuickFactLabel>
            <QuickFactValue>first-party (prest0.ai) only — we set no advertising or analytics cookies and load no third-party trackers</QuickFactValue>
          </QuickFactCard>
          <QuickFactCard>
            <QuickFactLabel>retention window</QuickFactLabel>
            <QuickFactValue>session-based, plus the sign-in token and the preferences your browser keeps for you</QuickFactValue>
          </QuickFactCard>
          <QuickFactCard>
            <QuickFactLabel>consent banner</QuickFactLabel>
            <QuickFactValue>none — there is nothing to consent to, because we set no non-essential cookies</QuickFactValue>
          </QuickFactCard>
        </QuickFactsGrid>

        <Callout>
          you will not meet a cookie wall here, and that is not an oversight. prest0 runs no advertising or analytics trackers at all, so there is no non-essential cookie to accept or decline and nothing for a banner to ask you. the only cookies we set are the ones that keep you signed in and remember how you have set things up. your browser controls remain the full set of controls.
        </Callout>

        <SectionTitle>how cookies align with our security controls</SectionTitle>
        <CookieText>
          Our security page outlines the baseline controls we run across every workspace. Those same safeguards govern how cookies and similar technologies work inside prest0:
        </CookieText>
        <CookieList>
          <CookieListItem>TLS 1.3 with HSTS keeps consent banners, preference APIs, and authentication cookies encrypted end to end.</CookieListItem>
          <CookieListItem>Data stored in Google Cloud (including consent logs) is protected with AES-256 encryption and managed key rotation.</CookieListItem>
          <CookieListItem>Enterprise OAuth 2.0 plus hardware-backed MFA guard access to cookie preference dashboards and admin tooling.</CookieListItem>
          <CookieListItem>The contractual no-training guarantee covers telemetry tied to cookies—prompts, transcripts, and analytics derived from them never fine-tune shared models.</CookieListItem>
          <CookieListItem>Log retention follows the same documented schedule as the rest of the platform. Self-service timers and automated deletion receipts are on the roadmap, not shipped.</CookieListItem>
          <CookieListItem>Immutable audit logs, SOC 2 TYPE II, and ISO/IEC 27001 inheritance ensure every consent or preference change is traceable.</CookieListItem>
        </CookieList>

        <SectionTitle>what are cookies?</SectionTitle>
        <CookieText>
          Cookies are small text files that websites place on your device to remember information about your visit. Some persist after you close your browser (persistent cookies) while others disappear when you end the session (session cookies). We rely on both types to keep the prest0 experience smooth, secure, and personalized.
        </CookieText>

        <SectionTitle>cookie categories we rely on</SectionTitle>
        <CookieText>
          We group cookies by how they support your workflow. The table below summarizes each category and how it shows up while you use prest0.
        </CookieText>

        <CookieTable>
          <thead>
            <tr>
              <CookieTableHeadCell>category</CookieTableHeadCell>
              <CookieTableHeadCell>how it supports your session</CookieTableHeadCell>
              <CookieTableHeadCell>examples you may see</CookieTableHeadCell>
            </tr>
          </thead>
          <tbody>
            <tr>
              <CookieTableCell>essential</CookieTableCell>
              <CookieTableCell>keeps you signed in, protects gated areas, remembers fundamental preferences, and routes traffic securely across our infrastructure.</CookieTableCell>
              <CookieTableCell>session tokens for secure login, load balancer routing identifiers, consent state flags.</CookieTableCell>
            </tr>
            <tr>
              <CookieTableCell>performance & analytics</CookieTableCell>
              <CookieTableCell>none. we run no analytics product on this site — no Google Analytics, no Segment, no PostHog, no session replay, no A/B testing allocator.</CookieTableCell>
              <CookieTableCell>nothing is set in this category.</CookieTableCell>
            </tr>
            <tr>
              <CookieTableCell>functionality</CookieTableCell>
              <CookieTableCell>remembers how you configure dashboards, the last agent you edited, and assists with faster reloads across sessions.</CookieTableCell>
              <CookieTableCell>saved layout preferences, agent builder state markers, locale or time-zone selectors.</CookieTableCell>
            </tr>
            <tr>
              <CookieTableCell>marketing</CookieTableCell>
              <CookieTableCell>none. we set no advertising cookie and embed no social or ad-network pixel — no Meta, no LinkedIn, no X.</CookieTableCell>
              <CookieTableCell>nothing is set in this category.</CookieTableCell>
            </tr>
          </tbody>
        </CookieTable>

        <SectionTitle>technologies we rely on</SectionTitle>
        <SubSectionTitle>browser cookies</SubSectionTitle>
        <CookieText>
          We set first-party cookies directly from prest0.ai to manage authentication, remember preferences, and stabilize navigation. Every cookie exchange rides over TLS 1.3 with HSTS, and the data is scoped to our domain so it inherits the same protections called out on the security page.
        </CookieText>
        <SubSectionTitle>local storage & indexeddb</SubSectionTitle>
        <CookieText>
          For real-time editing, drafts and layout we keep working state in local storage and IndexedDB. That data stays in your browser, is sent to our servers only over TLS 1.3, and is encrypted at rest on Google Cloud once it arrives. We do not encrypt it client-side before it leaves your browser, and we would rather say so than imply a protection we have not built.
        </CookieText>
        <SubSectionTitle>server-side logging</SubSectionTitle>
        <CookieText>
          Standard server logs capture IP address, browser metadata, and timestamps. Those logs sit on Google Cloud, inherit AES-256 encryption, roll up into append-only audit trails, and follow the documented retention schedule described in our security commitments.
        </CookieText>

        <SectionTitle>when we set cookies</SectionTitle>
        <CookieList>
          <CookieListItem>when you create or access your account to maintain a secure session.</CookieListItem>
          <CookieListItem>when you interact with the builder, dashboards, or monetization tools to remember your layout and selections.</CookieListItem>
        </CookieList>

        <SectionTitle>managing your preferences</SectionTitle>
        <CookieText>
          Most browsers allow you to control cookies through their settings. The exact location differs, but these steps cover the general flow:
        </CookieText>
        <CookieOrderedList>
          <CookieOrderedListItem>open your browser settings or preferences pane.</CookieOrderedListItem>
          <CookieOrderedListItem>look for privacy, security, or cookies controls.</CookieOrderedListItem>
          <CookieOrderedListItem>choose whether to block, clear, or allow cookies and site data.</CookieOrderedListItem>
          <CookieOrderedListItem>save your changes and refresh the prest0 tab.</CookieOrderedListItem>
        </CookieOrderedList>
        <CookieText>quick references for popular browsers:</CookieText>
        <CookieList>
          <CookieListItem><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data.</CookieListItem>
          <CookieListItem><strong>Safari:</strong> Settings &gt; Safari &gt; Privacy & Security or Preferences &gt; Privacy on macOS.</CookieListItem>
          <CookieListItem><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Manage and delete cookies and site data.</CookieListItem>
          <CookieListItem><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and site data.</CookieListItem>
        </CookieList>

        <SectionTitle>analytics and third-party cookies</SectionTitle>
        <CookieText>
          There are none. prest0.ai loads no analytics product, no advertising network and no social pixel, so there is no third-party cookie for you to opt out of and no vendor holding a record of your visit. We do not sell or share personal information, and nothing on this site feeds a shared AI model.
        </CookieText>
        <CookieText>
          The only third-party requests the site makes are for the fonts it renders in and the Google Cloud infrastructure it is served from. If that ever changes, it changes here first, with a new effective date, before the code ships.
        </CookieText>

        <SectionTitle>retention and refresh cycles</SectionTitle>
        <CookieText>
          Session cookies last as long as your session. Your sign-in token lasts until it expires or you sign out. Preferences your browser holds for you — layout, locale, the last thing you were working on — stay until you clear your browser data. There are no analytics or marketing cookies to expire, because none are set.
        </CookieText>

        <SectionTitle>do not track and global privacy control</SectionTitle>
        <CookieText>
          If your browser sends a Global Privacy Control or Do Not Track signal, we honor it — and in our case honoring it changes nothing, because we were never selling or sharing your personal information, running cross-site advertising, or setting a tracker for anyone to read. There is no state in which this site behaves differently depending on that header.
        </CookieText>

        <SectionTitle>updates to this policy</SectionTitle>
        <CookieText>
          We review this policy at least annually and whenever we introduce significant product changes or new third-party partnerships. Updates become effective once they appear on this page with a refreshed date stamp.
        </CookieText>
      </ContentContainer>

      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </PageContainer>
    {/* PromptBar removed — GlobalChatSystem will be added later */}
    </>
  );
};

export default Cookie;
